import React, { useEffect, useRef, useState } from "react";
import AppProvider from "../../api/appProvider";
import { useDesignDetailState } from "../../reducers/designdetails.reducer";
import { ButtonGroup } from "@blueprintjs/core";
import AtButton from "../atoms/AtButton";
import { useFullscreen, useToggle } from "react-use";
import classNames from "classnames";
import { pageViews } from "../../reducers/mainui.reducer";
import IntroLoader from "./components/IntroLoader";

function DesignCinematic(props) {
  const { logoUrl, close } = props;
  const designDetailState = useDesignDetailState();
  const cineamticContainerRef = useRef(null);

  const { designDetails, fullpath, hash, designDimsOrig } = designDetailState;
  const [fullscreenstatus, togglefullscreen] = useToggle(false);
  const isFullScreen = useFullscreen(cineamticContainerRef, fullscreenstatus, {
    onClose: () => togglefullscreen(false)
  });
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [serverCanvas, setServerCanvas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const animationStart = useRef(Date.now());

  const animationSequence = [
    { name: "Pan Up-Down", start: 0, end: 7.14 },
    { name: "Ken Burns Right", start: 7.14, end: 14.29 },
    { name: "Zoom Out Pan", start: 14.29, end: 21.43 },
    { name: "Pan Diagonal TR-BL", start: 21.43, end: 28.57 },
    { name: "Breathing", start: 28.57, end: 35.71 },
    { name: "Pan Left-Right", start: 35.71, end: 42.86 },
    { name: "Zoom In Pan", start: 42.86, end: 50 },
    { name: "Ken Burns Left", start: 50, end: 57.14 },
    { name: "Pan Down-Up", start: 57.14, end: 64.29 },
    { name: "Zoom In-Out", start: 64.29, end: 71.43 },
    { name: "Pan Diagonal TL-BR", start: 71.43, end: 78.57 },
    { name: "Pulse Pan", start: 78.57, end: 85.71 },
    { name: "Camera Dolly", start: 85.71, end: 92.86 },
    { name: "Pan Right-left", start: 92.86, end: 100 }
  ];

  // Load server-rendered canvas
  useEffect(() => {
    const getRenderedDesign = async () => {
      setLoading(true);
      const renderedCanvas = await AppProvider.getRenderedDesign({
        designDetails: { ...designDetails, ...designDimsOrig },
        fullpath,
        hash,
        zoom: 4,
        applyKLRatio: false
      });
      setServerCanvas(renderedCanvas);
      const timer = setTimeout(() => {
        setLoading(false);
      }, 2000); // 2 seconds loader, adjust as needed

      return () => clearTimeout(timer);
    };
    getRenderedDesign();
  }, [designDetails]);

  function drawCanvas() {
    const canvas = canvasRef.current;
    const wrapper = canvas.parentElement;
    if (!canvas || !wrapper) return;

    canvas.width = wrapper.clientWidth;
    canvas.height = wrapper.clientHeight;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const source = serverCanvas || imageRef.current;
    if (!source) return;

    const imgWidth = source.width;
    const imgHeight = source.height;

    const imgAspect = imgWidth / imgHeight;
    const canvasAspect = canvas.width / canvas.height;

    let drawWidth, drawHeight, offsetX, offsetY;

    if (imgAspect > canvasAspect) {
      // Image is wider → fit height (letterbox left/right)
      drawHeight = canvas.height;
      drawWidth = drawHeight * imgAspect;
      offsetX = (canvas.width - drawWidth) / 2;
      offsetY = 0;
    } else {
      // Image is taller → fit width (letterbox top/bottom)
      drawWidth = canvas.width;
      drawHeight = drawWidth / imgAspect;
      offsetX = 0;
      offsetY = (canvas.height - drawHeight) / 2;
    }

    ctx.drawImage(source, offsetX, offsetY, drawWidth, drawHeight);
  }

  // Animation loop
  useEffect(() => {
    let frame;
    function tick() {
      if (isPlaying) drawCanvas();
      frame = requestAnimationFrame(tick);
    }
    tick();
    return () => cancelAnimationFrame(frame);
  }, [isPlaying, serverCanvas, imageLoaded]);

  // Determine current animation mode
  const progress = (((Date.now() - animationStart.current) % 300000) / 300000) * 100;
  const currentMode =
    animationSequence.find(m => progress >= m.start && progress < m.end) ||
    animationSequence[animationSequence.length - 1];
  const topButtonGroupClass = classNames("topPanel hideOnIdle", { show: true });

  return (
    <div className="cinematic-container" ref={cineamticContainerRef}>
      <ButtonGroup className={topButtonGroupClass}>
        <AtButton
          minimal
          title={`${isFullScreen ? "Exit" : "Enter"} Fullscreen`}
          intent={null}
          icon={`${isFullScreen ? "exit" : "enter"}-fullscreen`}
          onClick={() => {
            togglefullscreen();
          }}
        />

        <AtButton
          minimal
          title={`Close`}
          intent={null}
          icon={`close`}
          onClick={() => close()}
        ></AtButton>
      </ButtonGroup>

      {loading && <IntroLoader logoUrl={logoUrl} />}

      <div className="canvas-wrapper">
        <canvas
          className={`cinematic-canvas cinematic-sequence ${isPlaying ? "play" : "pause"}`}
          ref={canvasRef}
        />
      </div>
    </div>
  );
}

export default DesignCinematic;
